import { createReducer, createSelector, on } from '@ngrx/store';
import {
  ClusterDto,
  GeoviewSummaryDto,
  VehicleDto,
} from '@services/api/api.service';
import * as Actions from './actions';
import { DealerGeoviewFilters, DealerGeoviewViewport } from './actions';
import * as RootActions from '../../actions';
import { DealerGeoviewClusterTile, mergeGeoviewClusters } from './cluster-tiles';

export interface DealerVehicleData {
  vehicle: VehicleDto;
  lastUpdate: string;
}

export interface DealerGeoviewClusterTileState {
  tile: DealerGeoviewClusterTile;
  clusters: ClusterDto[];
  status: 'loading' | 'loaded' | 'error';
  loadedAt: number | null;
  error: unknown;
}

export interface State {
  summary: GeoviewSummaryDto | null;
  clusterTiles: { [key: string]: DealerGeoviewClusterTileState };
  visibleClusterTileKeys: string[];
  dealerVehicles: { [vin: string]: DealerVehicleData };
  vehicleOrder: string[];
  selectedVehicle: VehicleDto | null;
  selectedVin: string | null;
  viewport: DealerGeoviewViewport | null;
  filtersDealer: DealerGeoviewFilters;
  automaticRefreshEnabled: boolean;
  withoutPositionFilterEnabled: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalDealerEntries: number;
  loadingSummary: boolean;
  loadingDealer: boolean;
  loadingSelectedVehicle: boolean;
  summaryError: unknown;
  vehiclePageError: unknown;
  selectedVehicleError: unknown;
}

export const initialState: State = {
  summary: null,
  clusterTiles: {},
  visibleClusterTileKeys: [],
  dealerVehicles: {},
  vehicleOrder: [],
  selectedVehicle: null,
  selectedVin: null,
  viewport: null,
  filtersDealer: {},
  automaticRefreshEnabled: false,
  withoutPositionFilterEnabled: false,
  page: 1,
  pageSize: 50,
  totalPages: 0,
  totalDealerEntries: 0,
  loadingSummary: false,
  loadingDealer: true,
  loadingSelectedVehicle: false,
  summaryError: null,
  vehiclePageError: null,
  selectedVehicleError: null,
};

export const selectDealerVehicles = state => state.geoviewDealer.dealerVehicles;
export const selectGeoviewDealerSummary = state => state.geoviewDealer.summary;
export const selectGeoviewDealerClusterTileCache =
  (state): { [key: string]: DealerGeoviewClusterTileState } => state.geoviewDealer.clusterTiles;
export const selectVisibleGeoviewDealerClusterTileKeys =
  (state): string[] => state.geoviewDealer.visibleClusterTileKeys;
export const selectVisibleGeoviewDealerClusterTiles = createSelector(
  selectGeoviewDealerClusterTileCache,
  selectVisibleGeoviewDealerClusterTileKeys,
  (clusterTiles, visibleTileKeys) => visibleTileKeys.map(key => clusterTiles[key]).filter(Boolean),
);
export const selectGeoviewDealerClusters = createSelector(
  selectVisibleGeoviewDealerClusterTiles,
  visibleTiles => mergeGeoviewClusters(visibleTiles.flatMap(tile => tile.clusters)),
);
export const selectGeoviewDealerViewport = state => state.geoviewDealer.viewport;
export const selectTotalVehiclesDealer = createSelector(
  selectGeoviewDealerSummary,
  summary => summary?.totalVehicles || 0,
);
export const selectActiveVehiclesDealer = createSelector(
  selectGeoviewDealerSummary,
  summary => summary?.activeVehicles || 0,
);
export const selectVehiclesWithoutPositionDealer = createSelector(
  selectGeoviewDealerSummary,
  summary => summary?.vehiclesWithoutPosition || 0,
);
export const selectTotalDealerEntries = state => state.geoviewDealer.totalDealerEntries;
export const selectLoadingGeoviewDealer = state => state.geoviewDealer.loadingDealer;
export const selectLoadingGeoviewDealerClusters = createSelector(
  selectVisibleGeoviewDealerClusterTiles,
  visibleTiles => visibleTiles.some(tile => tile.status === 'loading'),
);
export const selectFiltersDealer = state => state.geoviewDealer.filtersDealer;
export const selectGeoviewDealerAutomaticRefreshEnabled =
  state => state.geoviewDealer.automaticRefreshEnabled;
export const selectGeoviewDealerWithoutPositionFilterEnabled =
  state => state.geoviewDealer.withoutPositionFilterEnabled;
export const selectSelectedVinDealer = state => state.geoviewDealer.selectedVin;
export const selectGeoviewDealerPage = state => state.geoviewDealer.page;
export const selectGeoviewDealerPageSize = state => state.geoviewDealer.pageSize;
export const selectGeoviewDealerTotalPages = state => state.geoviewDealer.totalPages;

export const selectGeoviewDealerEntries = createSelector(
  selectDealerVehicles,
  vehicles => Object.values(vehicles).map((data: DealerVehicleData) => data.vehicle),
);

export const selectVehicleOrder = state => state.geoviewDealer.vehicleOrder;
export const selectGeoviewDealerKeys = createSelector(selectVehicleOrder, order => order);

export const selectDealerGeoviewSelectedEntry = state =>
  state.geoviewDealer.selectedVehicle ||
  state.geoviewDealer.dealerVehicles[state.geoviewDealer.selectedVin]?.vehicle ||
  null;

export const selectDealerVehicleByVin = (vin: string) => state =>
  state.geoviewDealer.dealerVehicles[vin]?.vehicle || null;

export const selectDealerVehicleDataByVin = (vin: string) => state =>
  state.geoviewDealer.dealerVehicles[vin] || null;

export const geoviewDealerReducer = createReducer(
  initialState,
  on(RootActions.resetState, () => ({ ...initialState })),
  on(Actions.loadGeoviewDealerSummary, state => ({
    ...state,
    loadingSummary: true,
    summaryError: null,
  })),
  on(Actions.loadGeoviewDealerSummarySuccess, (state, { summary }) => ({
    ...state,
    summary,
    loadingSummary: false,
  })),
  on(Actions.loadGeoviewDealerSummaryFailure, (state, { error }) => ({
    ...state,
    loadingSummary: false,
    summaryError: error,
  })),
  on(Actions.setGeoviewDealerViewport, (state, { viewport }) => ({
    ...state,
    viewport,
  })),
  on(Actions.loadGeoviewDealerClusterTiles, (state, { tiles }) => ({
    ...state,
    visibleClusterTileKeys: tiles.map(tile => tile.key),
  })),
  on(Actions.geoviewDealerClusterTileRequestsStarted, (state, { tiles }) => ({
    ...state,
    clusterTiles: tiles.reduce((clusterTiles, tile) => ({
      ...clusterTiles,
      [tile.key]: {
        tile,
        clusters: clusterTiles[tile.key]?.clusters || [],
        status: 'loading' as const,
        loadedAt: clusterTiles[tile.key]?.loadedAt || null,
        error: null,
      },
    }), state.clusterTiles),
  })),
  on(Actions.loadGeoviewDealerClusterTileSuccess, (state, { tile, clusters, loadedAt }) => {
    const clusterTiles = evictClusterTileCache({
      ...state.clusterTiles,
      [tile.key]: {
        tile,
        clusters,
        status: 'loaded',
        loadedAt,
        error: null,
      },
    }, state.visibleClusterTileKeys);
    return {
      ...state,
      clusterTiles,
    };
  }),
  on(Actions.loadGeoviewDealerClusterTileFailure, (state, { tile, error }) => ({
    ...state,
    clusterTiles: {
      ...state.clusterTiles,
      [tile.key]: {
        tile,
        clusters: state.clusterTiles[tile.key]?.clusters || [],
        status: 'error',
        loadedAt: state.clusterTiles[tile.key]?.loadedAt || null,
        error,
      },
    },
  })),
  on(Actions.loadGeoviewDealerVehiclePage, (state, { pageNumber, pageSize }) => ({
    ...state,
    page: pageNumber,
    pageSize,
    loadingDealer: true,
    vehiclePageError: null,
  })),
  on(Actions.loadGeoviewDealerVehiclePageSuccess, (state, { page }) => {
    const vehicles = page.vehicles || [];
    const dealerVehicles = vehicles.reduce((result, vehicle) => {
      if (vehicle.vin) {
        result[vehicle.vin] = {
          vehicle,
          lastUpdate: new Date().toISOString(),
        };
      }
      return result;
    }, {} as { [vin: string]: DealerVehicleData });
    const selectedVehicle = state.selectedVin && dealerVehicles[state.selectedVin]
      ? dealerVehicles[state.selectedVin].vehicle
      : state.selectedVehicle;

    return {
      ...state,
      dealerVehicles,
      vehicleOrder: vehicles.map(vehicle => vehicle.vin).filter(Boolean),
      selectedVehicle,
      page: page.currentPage || state.page,
      pageSize: page.pageSize || state.pageSize,
      totalPages: page.totalPages || 0,
      totalDealerEntries: page.totalCount || 0,
      loadingDealer: false,
    };
  }),
  on(Actions.loadGeoviewDealerVehiclePageFailure, (state, { error }) => ({
    ...state,
    loadingDealer: false,
    vehiclePageError: error,
  })),
  on(Actions.setGeoviewDealerSelected, (state, { value }) => {
    const selectedVin = typeof value === 'string' ? value : value?.vin || null;
    return {
      ...state,
      selectedVin,
      selectedVehicle: typeof value === 'string'
        ? state.dealerVehicles[value]?.vehicle || null
        : value || null,
    };
  }),
  on(Actions.loadGeoviewDealerVehicleByVin, (state, { cluster }) => ({
    ...state,
    selectedVin: cluster.vin,
    withoutPositionFilterEnabled: false,
    selectedVehicle: cluster.vin
      ? {
        vin: cluster.vin,
        name: cluster.name,
        latitude: cluster.latitude,
        longitude: cluster.longitude,
      }
      : null,
    loadingSelectedVehicle: true,
    selectedVehicleError: null,
  })),
  on(Actions.loadGeoviewDealerVehicleByVinSuccess, (state, { cluster, vehicle }) => ({
    ...state,
    selectedVin: vehicle.vin || cluster.vin,
    selectedVehicle: {
      ...vehicle,
      latitude: cluster.latitude,
      longitude: cluster.longitude,
    } as VehicleDto,
    loadingSelectedVehicle: false,
  })),
  on(Actions.loadGeoviewDealerVehicleByVinFailure, (state, { error }) => ({
    ...state,
    loadingSelectedVehicle: false,
    selectedVehicleError: error,
  })),
  on(Actions.setGeoviewDealerFilters, (state, { filters }) => ({
    ...state,
    filtersDealer: filters,
    clusterTiles: {},
    visibleClusterTileKeys: [],
    page: 1,
  })),
  on(Actions.setGeoviewDealerAutomaticRefresh, (state, { enabled }) => ({
    ...state,
    automaticRefreshEnabled: enabled,
  })),
  on(Actions.setGeoviewDealerWithoutPositionFilter, (state, { enabled }) => ({
    ...state,
    withoutPositionFilterEnabled: enabled,
    page: 1,
  })),
  on(Actions.dealerVehicleEngineStatusChanged, (state, { vin, engineOn }) => {
    const existingVehicle = state.dealerVehicles[vin];
    return {
      ...state,
      dealerVehicles: existingVehicle
        ? {
          ...state.dealerVehicles,
          [vin]: {
            ...existingVehicle,
            vehicle: { ...existingVehicle.vehicle, isRunning: engineOn },
          },
        }
        : state.dealerVehicles,
      selectedVehicle: state.selectedVehicle?.vin === vin
        ? { ...state.selectedVehicle, isRunning: engineOn }
        : state.selectedVehicle,
      clusterTiles: Object.fromEntries(Object.entries(state.clusterTiles).map(([key, tile]) => [
        key,
        {
          ...tile,
          clusters: tile.clusters.map(cluster => cluster.vin === vin && (cluster.vehicleCount || 0) === 1
            ? { ...cluster, activeVehicleCount: engineOn ? 1 : 0 }
            : cluster),
        },
      ])),
    };
  }),
  on(Actions.setDealerVehicleFavorite, (state, { vin, isFavorite }) => {
    const existingVehicle = state.dealerVehicles[vin];
    return {
      ...state,
      dealerVehicles: existingVehicle
        ? {
          ...state.dealerVehicles,
          [vin]: {
            ...existingVehicle,
            vehicle: { ...existingVehicle.vehicle, isFavorite },
          },
        }
        : state.dealerVehicles,
      selectedVehicle: state.selectedVehicle?.vin === vin
        ? { ...state.selectedVehicle, isFavorite }
        : state.selectedVehicle,
    };
  }),
);

function evictClusterTileCache(
  clusterTiles: { [key: string]: DealerGeoviewClusterTileState },
  visibleTileKeys: string[],
): { [key: string]: DealerGeoviewClusterTileState } {
  const maximumCachedTiles = 64;
  const tileKeys = Object.keys(clusterTiles);
  if (tileKeys.length <= maximumCachedTiles) {
    return clusterTiles;
  }

  const visibleKeys = new Set(visibleTileKeys);
  const removableKeys = tileKeys
    .filter(key => !visibleKeys.has(key))
    .sort((left, right) =>
      (clusterTiles[left].loadedAt || 0) - (clusterTiles[right].loadedAt || 0));
  const result = { ...clusterTiles };
  removableKeys
    .slice(0, Math.max(0, tileKeys.length - maximumCachedTiles))
    .forEach(key => delete result[key]);
  return result;
}
